import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaEnvelope, FaPhoneAlt, FaMapMarkerAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';

const FooterContainer = styled.footer`
  background: #111;
  color: #f5f5f5;
  padding: 4rem 0 2rem;
  position: relative;
  overflow: hidden;
  margin-top: 0;
  z-index: 10; /* Valor alto para asegurar que esté por encima de otros elementos */
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
    gap: 2rem;
    padding: 0 1rem;
  }
`;

const FooterColumn = styled.div`
  h3 {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
    font-weight: 500;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      left: 0;
      bottom: -10px;
      width: 50px;
      height: 2px;
      background: #007bff;
      
      @media (max-width: 768px) {
        left: 50%;
        transform: translateX(-50%);
      }
    }
  }
  
  p {
    line-height: 1.8;
    margin-bottom: 1.5rem;
    color: #aaa;
  }
  
  ul {
    list-style: none;
    padding: 0;
  }
  
  li {
    margin-bottom: 0.8rem;
  }
  
  a {
    color: #aaa;
    text-decoration: none;
    transition: color 0.3s;
    
    &:hover {
      color: #007bff;
    }
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  justify-content: flex-start;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const SocialIcon = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #222;
  color: #fff;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: #007bff;
    transform: translateY(-5px);
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  color: #aaa;
  
  svg {
    margin-right: 10px;
    color: #007bff;
  }
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding-top: 2rem;
  margin-top: 3rem;
  border-top: 1px solid #333;
  color: #aaa;
  font-size: 0.9rem;
  
  a {
    color: #007bff;
    text-decoration: none;
  }
`;

function Footer() {
  return (
    <FooterContainer>
      <FooterContent>
        <FooterColumn>
          <h3>Sobre Nosotros</h3>
          <p>Somos un equipo apasionado por transformar ideas en experiencias digitales únicas. Con más de 8 años de experiencia, ofrecemos soluciones personalizadas en diseño web, SEO y estrategias digitales.</p>
          <SocialIcons>
            <SocialIcon 
              href="https://facebook.com" 
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <FaFacebookF />
            </SocialIcon>
            <SocialIcon 
              href="https://instagram.com" 
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <FaInstagram />
            </SocialIcon>
            <SocialIcon 
              href="https://linkedin.com" 
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <FaLinkedinIn />
            </SocialIcon>
          </SocialIcons>
        </FooterColumn>
        
        <FooterColumn>
          <h3>Enlaces Rápidos</h3>
          <ul>
            <li><Link to="/">Inicio</Link></li>
            <li><Link to="/proyectos">Proyectos</Link></li>
            <li><Link to="/servicios">Servicios</Link></li>
            <li><Link to="/quienes-somos">Quiénes Somos</Link></li>
            <li><Link to="/contacto">Contacto</Link></li>
          </ul>
        </FooterColumn>
        
        <FooterColumn>
          <h3>Servicios</h3>
          <ul>
            <li><Link to="/servicios">Desarrollo Web Personalizado</Link></li>
            <li><Link to="/servicios">E-commerce Solutions</Link></li>
            <li><Link to="/servicios">Aplicaciones Web Progresivas</Link></li>
            <li><Link to="/servicios">Posicionamiento SEO Avanzado</Link></li>
            <li><Link to="/servicios">Diseño Gráfico & Branding</Link></li>
          </ul>
        </FooterColumn>
        
        <FooterColumn>
          <h3>Contacto</h3>
          <ContactItem>
            <FaMapMarkerAlt />
            <span>Calle San Benito 3, 04738 Vicar</span>
          </ContactItem>
          <ContactItem>
            <FaPhoneAlt />
            <span>+34 604 831 012</span>
          </ContactItem>
          <ContactItem>
            <FaEnvelope />
            <span>info@juanfrahernandez.es</span>
          </ContactItem>
        </FooterColumn>
      </FooterContent>
      
      <Copyright>
        <p>&copy; {new Date().getFullYear()} JuanFra Hernández. Todos los derechos reservados.</p>
        <p>Sí, la web mola. Sí, la hice yo. <a href="https://juanfrahernandez.es" target="_blank" rel="noopener noreferrer">JuanFra Hernández</a></p>
        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '1.5rem', fontSize: '0.85rem' }}>
          <Link to="/politica-privacidad">Política de Privacidad</Link>
          <Link to="/politica-cookies">Política de Cookies</Link>
          <Link to="/aviso-legal">Aviso Legal</Link>
          <Link to="/terminos-y-condiciones">Terminos y Condiciones de Dominios</Link>
        </div>
      </Copyright>
    </FooterContainer>
  );
}

export default Footer;